import { useState } from 'react';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useInvitationGifts,
  useAddGift,
  useDeleteGift,
} from '@/hooks/useInvitations';

interface Props {
  invitationId: string;
}

const GiftSuggestions = ({ invitationId }: Props) => {
  const { data: gifts } = useInvitationGifts(invitationId);
  const addGift = useAddGift();
  const deleteGift = useDeleteGift();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [store, setStore] = useState('');
  const [url, setUrl] = useState('');

  const handleAdd = async () => {
    if (!name) return;
    await addGift.mutateAsync({
      invitation_id: invitationId,
      gift_name: name,
      gift_description: description || undefined,
      gift_price: price ? parseFloat(price) : undefined,
      store_name: store || undefined,
      store_url: url || undefined,
    });
    setName('');
    setDescription('');
    setPrice('');
    setStore('');
    setUrl('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lista de Regalos Sugeridos</CardTitle>
        <CardDescription>
          Agrega artículos específicos que te gustaría recibir
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Nombre del regalo</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Juego de sábanas"
            />
          </div>
          <div className="space-y-2">
            <Label>Precio aproximado</Label>
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="1500"
            />
          </div>
          <div className="space-y-2">
            <Label>Tienda sugerida</Label>
            <Input
              value={store}
              onChange={(e) => setStore(e.target.value)}
              placeholder="Liverpool"
            />
          </div>
          <div className="space-y-2">
            <Label>Link del producto</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://tienda.com/producto"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Descripción</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Color blanco, tamaño king size"
          />
        </div>
        <Button type="button" onClick={handleAdd} disabled={addGift.isPending}>
          <Plus className="w-4 h-4 mr-2" />
          Agregar Regalo
        </Button>

        {gifts && gifts.length > 0 && (
          <div className="grid gap-3 mt-4">
            {gifts.map((gift) => (
              <div
                key={gift.id}
                className="flex items-center gap-4 p-3 bg-muted rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{gift.gift_name}</p>
                    {gift.is_reserved && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        Reservado
                      </span>
                    )}
                  </div>
                  {gift.gift_description && (
                    <p className="text-sm text-muted-foreground">
                      {gift.gift_description}
                    </p>
                  )}
                  <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                    {gift.gift_price && <span>${gift.gift_price}</span>}
                    {gift.store_name && <span>{gift.store_name}</span>}
                  </div>
                </div>
                {gift.store_url && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={gift.store_url} target="_blank" rel="noopener">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() =>
                    deleteGift.mutate({ id: gift.id, invitationId })
                  }
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GiftSuggestions;
